import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  SUBSCRIPTION_CONFIG,
  businessDisplayName,
  computeSubscriptionPreview,
  getPlanBusiness,
  isContactSalesPlan,
  planUnit,
  roundPrice,
  usageOrderTiers,
  usagePriceDisplayMode,
  type SubscriptionAudience,
  type SubscriptionInterval,
  type UsagePriceDisplayMode,
} from '@hero/core';
import type { Business, Currency, Language } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { CheckIcon, SparklesIcon } from '@hero/web-icons';
import { Price } from '../../../app/Price';
import { Button } from '../../../ui/Button/Button';

/**
 * Visual tier of a plan card. Drives a coordinated set of
 * background, border, text, and accent classes so the row reads
 * as a left-to-right progression (entry → recommended → enterprise).
 */
export type PlanCardTier = 'soft' | 'brand' | 'premium';

export interface PlanCardProps {
  planCode: string;
  selected: boolean;
  onSelect: () => void;
  interval: SubscriptionInterval;
  currency: Currency;
  /**
   * Number of committed vehicle seats the user has selected on the
   * shared stepper. Used to compute an apples-to-apples
   * "per truck / month" price for this card so users can compare
   * plans regardless of the active interval.
   */
  committedVehicleQty: number;
  /**
   * Committed monthly order volume from the shared stepper. Both
   * quantities are passed to every card; each card reads whichever its
   * own plan meters, so a per-order card can quote a real per-order
   * price while sitting next to seat-based cards.
   */
  committedOrderQty?: number;
  /**
   * Which ladder this card belongs to. Only read for credit plans,
   * whose price lives on the business's volume ladder rather than on the
   * plan.
   */
  business?: Business;
  /**
   * Whose price list to read the ladder from. Same reason as `business`:
   * the rate is not on the plan, it is on the ladder, and an
   * owner-operator's ladder is not a company's.
   */
  audience?: SubscriptionAudience;
  /** Sales/marketing label shown over the price row. */
  title: string;
  /** Short one-line pitch under the title. */
  description: string;
  /**
   * Badge variant. `popular` paints a gradient ribbon at the top
   * right; `free_forever` shows a green pill in the header.
   */
  badge?: 'popular' | 'free_forever' | null;
  /**
   * Visual tier. `soft` for entry-level (white, clean), `brand`
   * for the recommended plan (primary-tinted gradient), `premium`
   * for enterprise (dark slate gradient).
   */
  tier?: PlanCardTier;
  /** When true, swap the price block for a "Contact sales" outline button. */
  isCustom?: boolean;
  onContactSales?: () => void;
}

interface TierStyle {
  surface: string;
  title: string;
  description: string;
  separator: string;
  priceText: string;
  intervalText: string;
  featureText: string;
  featureCheck: string;
  customPrimary: string;
  customSub: string;
  badgePopular: string;
  customButton: string;
}

const TIER_STYLES: Record<PlanCardTier, TierStyle> = {
  soft: {
    surface:
      'border-gray-200 bg-gradient-to-br from-white via-white to-gray-50/60',
    title: 'text-gray-900',
    description: 'text-gray-500',
    separator: 'border-gray-100',
    priceText: 'text-gray-900',
    intervalText: 'text-gray-500',
    featureText: 'text-gray-700',
    featureCheck: 'text-emerald-500',
    customPrimary: 'text-gray-900',
    customSub: 'text-gray-500',
    badgePopular: '',
    customButton: '',
  },
  brand: {
    surface:
      'border-primary/30 bg-gradient-to-br from-primary/[0.10] via-primary/[0.04] to-white',
    title: 'text-gray-900',
    description: 'text-gray-600',
    separator: 'border-primary/15',
    priceText: 'text-gray-900',
    intervalText: 'text-gray-500',
    featureText: 'text-gray-700',
    featureCheck: 'text-primary',
    customPrimary: 'text-gray-900',
    customSub: 'text-gray-500',
    badgePopular:
      'from-primary to-indigo-600 text-white shadow-md ring-1 ring-white/20',
    customButton: '',
  },
  premium: {
    surface:
      'border-gray-800 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800 text-white',
    title: 'text-white',
    description: 'text-gray-300',
    separator: 'border-white/10',
    priceText: 'text-white',
    intervalText: 'text-gray-400',
    featureText: 'text-gray-200',
    featureCheck: 'text-emerald-400',
    customPrimary: 'text-white',
    customSub: 'text-gray-400',
    badgePopular: '',
    customButton:
      'border-white/30 bg-white text-gray-900 hover:bg-gray-100 hover:text-gray-900',
  },
};

/**
 * Single plan tile inside the checkout's plan picker.
 *
 * Reads its features directly from `SUBSCRIPTION_CONFIG.plans[planCode]`
 * so we never drift from what the backend will actually grant.
 * Visual look is driven by `tier`, not `planCode`, so the three
 * cards on the row form a coordinated entry → brand → premium
 * progression regardless of which codes are present.
 */
export function PlanCard({
  planCode,
  selected,
  onSelect,
  interval,
  currency,
  committedVehicleQty,
  committedOrderQty,
  business = 'hero',
  audience = 'org',
  title,
  description,
  badge,
  tier = 'soft',
  isCustom,
  onContactSales,
}: PlanCardProps) {
  const { t, i18n } = useTranslation('m-billing');
  const features = buildFeatureList(planCode, t);
  const s = TIER_STYLES[tier];

  // A credit card quotes the rate at the volume that *earns* it, not
  // at whatever is currently on the stepper. Pricing all three at the
  // shared quantity would print the same number three times and
  // flatten the ladder into a row of identical cards — the difference
  // between the tiers is the whole argument.
  const tierMinQty = usageOrderTiers(business, audience).find(t => t.planCode === planCode)?.minQty;

  // Compute this card's own preview against the same quantities +
  // interval the user has selected on the shared controls, so the
  // headline unit price reads on the same axis as every other card in
  // the row.
  const preview = computeSubscriptionPreview({
    planCode,
    interval,
    committedVehicleQty,
    committedOrderQty: tierMinQty ?? committedOrderQty,
    currency,
    business,
    audience,
  });

  const planBusiness = getPlanBusiness(planCode) ?? business;
  const displayMode =
    preview?.billingBasis === 'per_order' && planBusiness
      ? usagePriceDisplayMode(planBusiness, audience)
      : 'per_unit';

  // Card root is intentionally a `<div role="button">` (not a `<button>`):
  // the `custom` variant nests a real `<Button>` ("Contact sales") and
  // having a `<button>` inside a `<button>` is invalid HTML — React 19
  // throws a hydration error, and the inner click never bubbles cleanly.
  // We keep button-equivalent UX (tabIndex + Enter/Space activation +
  // `aria-pressed`) so screen readers and keyboards still treat it as
  // a toggle. The inner Contact-sales button stops propagation so a
  // click on it doesn't double-fire `onSelect`.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      aria-pressed={selected}
      className={cn(
        'group relative flex h-full w-full cursor-pointer flex-col gap-4 rounded-2xl border p-5 text-start transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        s.surface,
        // Selected state: thin primary ring + a stacked primary-tinted
        // outer glow that hugs the rounded corners and reads as a
        // "shining" rim — bright next to the border, fading into a
        // soft ambient outer glow. No bleeding halo, no content tint.
        //   1) 5px soft halo right outside the ring (immediate rim)
        //   2) 16px bright primary glow (the "shine")
        //   3) 32px wider ambient (softens the edge of the glow)
        //   4) 30px drop shadow with primary tint (depth + lift)
        selected
          ? '-translate-y-1 ring-[3px] ring-primary shadow-[0_0_0_5px_hsl(var(--primary)/0.20),0_0_16px_2px_hsl(var(--primary)/0.55),0_0_32px_4px_hsl(var(--primary)/0.35),0_12px_30px_-6px_hsl(var(--primary)/0.55)]'
          : 'shadow-sm hover:-translate-y-0.5 hover:shadow-md',
      )}>
      {selected && (
        <span
          aria-hidden
          className="absolute -start-2 -top-2 inline-flex size-7 items-center justify-center rounded-full bg-primary text-white shadow-md ring-4 ring-white">
          <CheckIcon className="size-4" />
        </span>
      )}

      {badge === 'popular' && (
        <span
          className={cn(
            'absolute -top-3 end-4 inline-flex items-center gap-1 rounded-full bg-gradient-to-r px-3 py-1 text-[11px] font-semibold uppercase tracking-wider',
            s.badgePopular || 'from-primary to-indigo-600 text-white shadow-md',
          )}>
          <SparklesIcon className="size-3" />
          {t('plan_card.badge.popular')}
        </span>
      )}

      <header className="flex items-start justify-between gap-2">
        <div className="flex flex-col">
          {/* Which brand the plan belongs to. "Start" and "Pro"
              are tier names both businesses reuse, so the tier alone does
              not tell a buyer what they are subscribing to. */}
          <span
            className={cn(
              'text-[11px] font-semibold uppercase tracking-wider',
              s.description,
            )}>
            {businessDisplayName(
              getPlanBusiness(planCode) ?? business,
              i18n.language as Language,
            )}
          </span>
          <h3 className={cn('text-base font-semibold', s.title)}>{title}</h3>
          <p className={cn('mt-0.5 text-xs', s.description)}>{description}</p>
        </div>
        {badge === 'free_forever' && (
          <span className="shrink-0 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
            {t('plan_card.badge.free_forever')}
          </span>
        )}
      </header>

      <PriceBlock
        isCustom={isCustom}
        preview={preview}
        currency={currency}
        interval={interval}
        fromQty={tierMinQty}
        displayMode={displayMode}
        priceClass={s.priceText}
        intervalClass={s.intervalText}
        customPrimary={s.customPrimary}
        customSub={s.customSub}
        t={t}
      />

      <ul className={cn('flex flex-col gap-2 border-t pt-4', s.separator)}>
        {features.map(f => (
          <li
            key={f}
            className={cn('flex items-start gap-2 text-[13px]', s.featureText)}>
            <CheckIcon className={cn('mt-0.5 size-4 shrink-0', s.featureCheck)} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isCustom && (
        <Button
          variant="outline"
          onClick={e => {
            // The outer card is `role=button` with its own click
            // handler; without `stopPropagation` clicking "Contact
            // sales" would also fire `onSelect` and toggle selection
            // out from under the user.
            e.stopPropagation();
            onContactSales?.();
          }}
          className={cn('mt-auto w-full', s.customButton)}>
          {t('plan_card.custom.contact_sales')}
        </Button>
      )}
    </div>
  );
}

interface PriceBlockProps {
  isCustom?: boolean;
  /**
   * Canonical preview for this card. `null` only when the planCode is
   * unknown (caller bug). For `free` the preview's `isFree` flag drives
   * the "no card required" copy.
   */
  preview: ReturnType<typeof computeSubscriptionPreview>;
  currency: Currency;
  interval: SubscriptionInterval;
  /**
   * Volume that unlocks this rate, on a credit plan. Replaces the
   * billed-cycle subtext, which has nothing to say about a purchase
   * with no cycle.
   */
  fromQty?: number;
  displayMode?: UsagePriceDisplayMode;
  priceClass: string;
  intervalClass: string;
  customPrimary: string;
  customSub: string;
  t: TFunction;
}

/**
 * Card-level price headline.
 *
 * Always displays an **apples-to-apples** unit price so users can compare
 * plans regardless of which billing cycle they have selected on the
 * shared toggle. The unit follows the plan's basis — "per truck / month"
 * on a seat plan, "per order" on a volume plan — unless the audience's
 * `displayMode` is `packaged`, in which case the headline is the rung
 * floor times the unit rate ("35 for 5 orders"). Charge is still per
 * credit. The actual interval (and what they'll be charged on day one)
 * is conveyed by the secondary "billed X" line.
 *
 * Variants:
 * - `custom`  → "Contact sales" copy (no number).
 * - free plan → "No card required" copy (no number).
 * - paid plan → big per-unit number + billed-cycle subtext.
 */
function PriceBlock({
  isCustom,
  preview,
  currency,
  interval,
  fromQty,
  displayMode = 'per_unit',
  priceClass,
  intervalClass,
  customPrimary,
  customSub,
  t,
}: PriceBlockProps) {
  if (isCustom) {
    return (
      <div className="flex flex-col">
        <span className={cn('text-xl font-semibold', customPrimary)}>
          {t('plan_card.custom.title')}
        </span>
        <span className={cn('text-xs', customSub)}>
          {t('plan_card.custom.subtitle')}
        </span>
      </div>
    );
  }
  if (!preview || preview.isFree) {
    return (
      <div className="flex flex-col">
        <Price amount={0} currency={currency} className={cn('text-2xl font-bold', priceClass)} />
        <span className={cn('text-xs', intervalClass)}>
          {t('plan_card.no_card_required')}
        </span>
      </div>
    );
  }
  const packaged = displayMode === 'packaged' && fromQty !== undefined;
  const headlineAmount = packaged
    ? roundPrice(preview.perUnitPrice * fromQty)
    : preview.perUnitPrice;
  const suffix = packaged
    ? t('plan_card.pack_of_qty_suffix', { count: fromQty })
    : preview.billingBasis === 'per_order'
      ? t('plan_card.per_order_suffix')
      : t('plan_card.per_truck_per_month_suffix');

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-baseline gap-1.5">
        <Price
          amount={headlineAmount}
          currency={currency}
          className={cn('text-2xl font-bold', priceClass)}
        />
        <span className={cn('text-xs font-medium', intervalClass)}>{suffix}</span>
      </div>
      <span className={cn('text-xs', intervalClass)}>
        {fromQty !== undefined
          ? t('plan_card.from_qty_label', { count: fromQty })
          : t(`plan_card.billed_label.${interval}`)}
      </span>
    </div>
  );
}

function buildFeatureList(planCode: string, t: TFunction): string[] {
  const plan = SUBSCRIPTION_CONFIG.plans[planCode];
  if (!plan) return [];
  const features: string[] = [];
  const modules = plan.features.modules;

  if (planUnit(planCode) === 'order') {
    // Trucks cost nothing on a volume plan — you pay per shipment — so
    // "unlimited vehicles" is a genuine selling point here rather than
    // the fallback it is on a seat plan. Included volume is the
    // commitment the buyer picks, so it isn't a plan feature; only a
    // hard cap would be.
    features.push(t('plan_card.features.unlimited_vehicles'));
    if (plan.features.maxOrders) {
      features.push(t('plan_card.features.up_to_n_orders', { count: plan.features.maxOrders }));
    }
  } else if (plan.features.maxVehicles) {
    features.push(
      t('plan_card.features.up_to_n_vehicles', { count: plan.features.maxVehicles }),
    );
  } else if (plan.features.maxVehiclesPolicy === 'soft') {
    features.push(t('plan_card.features.unlimited_vehicles'));
  }
  if (modules?.order) features.push(t('plan_card.features.module_order'));
  if (modules?.fleet) features.push(t('plan_card.features.module_fleet'));
  if (modules?.crm) features.push(t('plan_card.features.module_crm'));
  if (modules?.api) features.push(t('plan_card.features.module_api'));

  // Support level comes off the plan's own config, so a new tier in
  // `SUBSCRIPTION_CONFIG` carries its promise onto the card without an
  // edit here. This used to be a chain of `planCode ===` branches, and
  // the per-order ladder shipped through it with no support bullet.
  if (plan.features.supportTier) {
    features.push(t(`plan_card.features.support_${plan.features.supportTier}`));
  }
  // An SLA is a contract clause, not a tier — only the negotiated plan
  // can promise one.
  if (isContactSalesPlan(planCode)) features.push(t('plan_card.features.custom_sla'));

  return features;
}
